import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Send, Hash, Search, MessageSquare, Smile, Reply, X } from 'lucide-react';
import Header from '../components/Header';
import SEO from '../components/SEO';
import { Avatar } from '../components/community/MemberCard';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const PALETTE = ['#4f46e5', '#0d9488', '#b45309', '#be123c', '#7c3aed', '#0369a1', '#15803d', '#c2410c'];
const hashOf = (s) => { let h = 0; for (const c of (s || '')) h = (h * 31 + c.charCodeAt(0)) >>> 0; return h; };
const decorate = (m) => {
  const parts = (m.name || m.from_name || '?').split(' ').filter(Boolean);
  const initials = ((parts[0]?.[0] || '?') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
  return { ...m, name: m.name || m.from_name, initials, color: m.color || PALETTE[hashOf(m.user_id || m.from_user_id || m.name) % PALETTE.length] };
};
const timeOf = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };
const EMOJIS = ['👍', '❤️', '😂', '🎉', '🙌', '👀'];

const ReactionChips = ({ reactions, myId, onToggle }) => {
  const entries = Object.entries(reactions || {}).filter(([, u]) => (u || []).length > 0);
  if (!entries.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1" data-testid="reaction-chips">
      {entries.map(([emoji, users]) => {
        const mine = (users || []).includes(myId);
        return (
          <button
            key={emoji}
            onClick={() => onToggle(emoji)}
            data-testid={`reaction-chip-${emoji}`}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs border transition-colors ${mine ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'}`}
          >
            <span>{emoji}</span><span className="text-[10px] font-semibold">{users.length}</span>
          </button>
        );
      })}
    </div>
  );
};

const MsgToolbar = ({ onReact, onReply, align = 'left' }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`relative flex items-center ${align === 'right' ? 'flex-row-reverse' : ''}`}>
      <div className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex items-center gap-0.5 bg-white border border-slate-200 rounded-full px-1 py-0.5 shadow-sm">
        <button onClick={() => setOpen((o) => !o)} data-testid="msg-react-btn" className="p-1 text-slate-500 hover:text-slate-900 rounded-full hover:bg-slate-100" aria-label="React"><Smile className="w-3.5 h-3.5" /></button>
        <button onClick={onReply} data-testid="msg-reply-btn" className="p-1 text-slate-500 hover:text-slate-900 rounded-full hover:bg-slate-100" aria-label="Reply"><Reply className="w-3.5 h-3.5" /></button>
      </div>
      {open && (
        <div className="absolute top-7 z-20 flex items-center gap-0.5 bg-white border border-slate-200 rounded-full px-1.5 py-1 shadow-lg" data-testid="emoji-picker">
          {EMOJIS.map((e) => (
            <button key={e} onClick={() => { onReact(e); setOpen(false); }} data-testid={`emoji-${e}`} className="text-base leading-none px-1 hover:scale-125 transition-transform">{e}</button>
          ))}
        </div>
      )}
    </div>
  );
};

const ReplyQuote = ({ author, preview }) => (
  <div className="border-l-2 border-slate-300 pl-2 mb-1 text-[11px] text-slate-500 max-w-full truncate">
    <span className="font-semibold text-slate-600">{author || 'Member'}</span>: {preview}
  </div>
);

const CommunityInboxPage = () => {
  const { user, loading, isMember } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [channels, setChannels] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null); // {type:'channel'|'dm', id?, user_id?, ...}
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState('');
  const [q, setQ] = useState('');
  const [tab, setTab] = useState('channels');
  const [replyTo, setReplyTo] = useState(null);
  const scrollRef = useRef(null);

  const loadConversations = useCallback(async () => {
    try { const r = await fetch(`${API}/api/community/conversations`, { credentials: 'include' }); if (r.ok) setConversations(((await r.json()).conversations || []).map(decorate)); } catch (_) {}
  }, []);
  const loadDirectory = useCallback(async () => {
    try { const r = await fetch(`${API}/api/community/directory`, { credentials: 'include' }); if (r.ok) setDirectory(((await r.json()).members || []).map(decorate)); } catch (_) {}
  }, []);
  const loadChannels = useCallback(async () => {
    try { const r = await fetch(`${API}/api/community/channels`, { credentials: 'include' }); if (r.ok) setChannels((await r.json()).channels || []); } catch (_) {}
  }, []);
  const loadThread = useCallback(async (otherId) => {
    try { const r = await fetch(`${API}/api/community/thread/${otherId}`, { credentials: 'include' }); if (r.ok) { const d = await r.json(); setMessages(d.messages || []); if (d.other) setActive({ type: 'dm', ...decorate(d.other) }); } } catch (_) {}
  }, []);
  const loadChannel = useCallback(async (id) => {
    try { const r = await fetch(`${API}/api/community/channel/${id}`, { credentials: 'include' }); if (r.ok) { const d = await r.json(); setMessages(d.messages || []); } } catch (_) {}
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!isMember) { navigate('/community'); return; }
    loadChannels(); loadConversations(); loadDirectory();
  }, [loading, isMember, navigate, loadChannels, loadConversations, loadDirectory]);

  // deep links: ?to=userId (dm) or ?c=channel
  useEffect(() => {
    if (!isMember) return;
    const to = params.get('to'); const c = params.get('c');
    if (to) { setActive({ type: 'dm', user_id: to }); setTab('messages'); loadThread(to); }
    else if (c) { setActive({ type: 'channel', id: c }); setTab('channels'); loadChannel(c); }
    else { setActive({ type: 'channel', id: 'general' }); setTab('channels'); loadChannel('general'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMember]);

  useEffect(() => {
    if (!isMember) return;
    const iv = setInterval(() => {
      loadConversations();
      if (active?.type === 'dm' && active.user_id) loadThread(active.user_id);
      else if (active?.type === 'channel') loadChannel(active.id);
    }, 4000);
    return () => clearInterval(iv);
  }, [isMember, active, loadConversations, loadThread, loadChannel]);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, active]);

  const openChannel = (id) => { setActive({ type: 'channel', id }); setMessages([]); setTab('channels'); loadChannel(id); setParams({ c: id }); };
  const openDM = (m) => { setActive({ type: 'dm', ...m }); setMessages([]); setTab('messages'); loadThread(m.user_id); setParams({ to: m.user_id }); };

  const send = async () => {
    if (!body.trim() || !active) return;
    const text = body.trim(); setBody('');
    const reply = replyTo ? { reply_to: replyTo.msg_id, reply_preview: replyTo.body, reply_author: replyTo.from_name || 'Member' } : {};
    setReplyTo(null);
    try {
      if (active.type === 'channel') {
        const r = await fetch(`${API}/api/community/channel/${active.id}`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ body: text, ...reply }) });
        if (r.ok) { const d = await r.json(); setMessages((m) => [...m, d.message]); loadChannels(); }
      } else {
        const r = await fetch(`${API}/api/community/thread/${active.user_id}`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ to_member_id: active.user_id, body: text, ...reply }) });
        if (r.ok) { const d = await r.json(); setMessages((m) => [...m, d.message]); loadConversations(); }
      }
    } catch (_) {}
  };

  const react = async (m, emoji) => {
    if (!m.msg_id || !active) return;
    const scope = active.type === 'channel' ? 'channel' : 'dm';
    try {
      const r = await fetch(`${API}/api/community/react`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ scope, msg_id: m.msg_id, emoji }) });
      if (r.ok) {
        const d = await r.json();
        setMessages((msgs) => msgs.map((x) => (x.msg_id === m.msg_id ? { ...x, reactions: d.reactions } : x)));
      }
    } catch (_) {}
  };

  const activeChannel = active?.type === 'channel' ? channels.find((c) => c.id === active.id) || { label: active.id } : null;
  const dirFiltered = directory.filter((m) => !q || (m.name || '').toLowerCase().includes(q.toLowerCase()) || (m.company || '').toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO title="Member inbox" description="Channels and direct messages for vetted members." path="/community/inbox" noindex />
      <Header />
      <main className="pt-8 pb-16">
        <div className="max-w-6xl mx-auto px-6">
          <button onClick={() => navigate('/community')} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-5"><ArrowLeft className="w-4 h-4" /> Community</button>
          <div className="grid grid-cols-1 md:grid-cols-12 bg-white border border-slate-200 rounded-2xl overflow-hidden" style={{ height: '74vh' }}>
            {/* Sidebar */}
            <div className="md:col-span-4 lg:col-span-3 bg-slate-900 text-slate-300 flex flex-col min-h-0">
              <div className="px-4 py-3 border-b border-slate-800 text-white font-bold">Community</div>

              {/* Tabs */}
              <div className="px-3 pt-3">
                <div className="grid grid-cols-2 gap-1 p-1 bg-slate-800/70 rounded-lg" data-testid="inbox-tabs">
                  <button
                    onClick={() => setTab('channels')}
                    data-testid="inbox-tab-channels"
                    className={`flex items-center justify-center gap-1.5 py-2 rounded-md text-xs font-semibold transition-colors ${tab === 'channels' ? 'bg-white text-slate-900' : 'text-slate-300 hover:text-white'}`}
                  >
                    <Hash className="w-3.5 h-3.5" /> Channels
                  </button>
                  <button
                    onClick={() => setTab('messages')}
                    data-testid="inbox-tab-messages"
                    className={`flex items-center justify-center gap-1.5 py-2 rounded-md text-xs font-semibold transition-colors ${tab === 'messages' ? 'bg-white text-slate-900' : 'text-slate-300 hover:text-white'}`}
                  >
                    <MessageSquare className="w-3.5 h-3.5" /> Messages
                  </button>
                </div>
                <p className="text-[11px] text-slate-400 leading-snug mt-2 px-1">
                  {tab === 'channels'
                    ? 'Channels are group conversations everyone in the room can see.'
                    : 'Direct messages are private 1:1 conversations with a member.'}
                </p>
              </div>

              <div className="flex-1 overflow-y-auto px-2 py-3 space-y-5" data-testid="inbox-sidebar">
                {tab === 'channels' ? (
                  /* Channels */
                  <div>
                    <div className="px-2 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-1">Group channels</div>
                    {channels.map((c) => (
                      <button key={c.id} onClick={() => openChannel(c.id)} data-testid={`channel-${c.id}`} className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm ${active?.type === 'channel' && active.id === c.id ? 'bg-white/15 text-white font-semibold' : 'hover:bg-white/5'}`}>
                        <Hash className="w-4 h-4 opacity-60" /> <span className="truncate">{c.label}</span>
                        {c.count > 0 && <span className="ml-auto text-[10px] text-slate-500">{c.count}</span>}
                      </button>
                    ))}
                  </div>
                ) : (
                  <>
                    {/* DMs */}
                    <div>
                      <div className="px-2 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-1">Your conversations</div>
                      {conversations.length === 0 && <div className="px-2 text-xs text-slate-500">No direct messages yet. Start one from Members below.</div>}
                      {conversations.map((m) => (
                        <button key={m.user_id} onClick={() => openDM(m)} data-testid={`dm-${m.user_id}`} className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm ${active?.type === 'dm' && active.user_id === m.user_id ? 'bg-white/15 text-white font-semibold' : 'hover:bg-white/5'}`}>
                          {m.picture ? <img src={m.picture} alt="" className="w-5 h-5 rounded-full object-cover" /> : <span className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] text-white" style={{ backgroundColor: m.color }}>{m.initials}</span>}
                          <span className="truncate">{m.name}</span>
                          {m.unread > 0 && <span className="ml-auto text-[10px] font-bold text-white bg-red-500 rounded-full px-1.5">{m.unread}</span>}
                        </button>
                      ))}
                    </div>
                    {/* Members */}
                    <div>
                      <div className="px-2 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-1">Start a new message</div>
                      <div className="px-2 mb-2 relative">
                        <Search className="w-3.5 h-3.5 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2" />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a member" data-testid="inbox-search" className="w-full pl-7 pr-2 py-1.5 bg-slate-800 border border-slate-700 rounded-md text-xs text-white placeholder:text-slate-500 focus:outline-none" />
                      </div>
                      {dirFiltered.map((m) => (
                        <button key={m.user_id} onClick={() => openDM(m)} data-testid={`member-${m.user_id}`} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-white/5">
                          {m.picture ? <img src={m.picture} alt="" className="w-5 h-5 rounded-full object-cover" /> : <span className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] text-white" style={{ backgroundColor: m.color }}>{m.initials}</span>}
                          <span className="truncate">{m.name}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Main pane */}
            <div className="md:col-span-8 lg:col-span-9 flex flex-col min-h-0">
              {!active ? (
                <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">Pick a channel or member.</div>
              ) : (
                <>
                  <div className="px-5 py-3 border-b border-slate-100">
                    {active.type === 'channel' ? (
                      <div className="flex items-center gap-2">
                        <Hash className="w-5 h-5 text-slate-400" />
                        <div><div className="font-bold text-slate-900">{activeChannel?.label}</div>{activeChannel?.desc && <div className="text-xs text-slate-500">{activeChannel.desc}</div>}</div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        {active.picture ? <img src={active.picture} alt="" className="w-9 h-9 rounded-full object-cover" /> : <Avatar member={decorate(active)} size={36} />}
                        <div><div className="font-semibold text-slate-900 text-sm">{active.name || 'Member'}</div><div className="text-xs text-slate-500">{[active.title, active.company].filter(Boolean).join(' · ')}</div></div>
                      </div>
                    )}
                  </div>

                  <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 bg-slate-50/50" data-testid="inbox-thread">
                    {messages.length === 0 ? (
                      <div className="text-center text-sm text-slate-400 pt-10">{active.type === 'channel' ? 'Be the first to post here.' : 'Say hello 👋'}</div>
                    ) : active.type === 'channel' ? (
                      <div className="space-y-4">
                        {messages.map((m, i) => {
                          const dm = decorate(m);
                          return (
                            <div key={m.msg_id || i} className="group flex gap-3">
                              {m.from_picture ? <img src={m.from_picture} alt="" className="w-9 h-9 rounded-lg object-cover shrink-0" /> : <span className="w-9 h-9 rounded-lg flex items-center justify-center text-xs text-white shrink-0" style={{ backgroundColor: dm.color }}>{dm.initials}</span>}
                              <div className="min-w-0 flex-1">
                                <div className="flex items-baseline gap-2"><span className="font-semibold text-slate-900 text-sm">{m.from_name || 'Member'}</span>{(m.from_title) && <span className="text-[11px] text-slate-400 truncate">{m.from_title}{m.from_company ? ` · ${m.from_company}` : ''}</span>}<span className="text-[11px] text-slate-400">{timeOf(m.sent_at)}</span></div>
                                {m.reply_to && <ReplyQuote author={m.reply_author} preview={m.reply_preview} />}
                                <div className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap break-words">{m.body}</div>
                                <ReactionChips reactions={m.reactions} myId={user?.user_id} onToggle={(e) => react(m, e)} />
                              </div>
                              <div className="shrink-0 self-start pt-4">
                                <MsgToolbar onReact={(e) => react(m, e)} onReply={() => setReplyTo(m)} align="right" />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {messages.map((m, i) => {
                          const mine = m.from_user_id === user?.user_id;
                          return (
                            <div key={m.msg_id || i} className={`group flex items-center gap-2 ${mine ? 'justify-end' : 'justify-start'}`}>
                              {mine && <MsgToolbar onReact={(e) => react(m, e)} onReply={() => setReplyTo(m)} align="right" />}
                              <div className={`flex flex-col ${mine ? 'items-end' : 'items-start'} max-w-[70%]`}>
                                <div className={`px-4 py-2.5 rounded-2xl text-sm ${mine ? 'bg-slate-900 text-white rounded-br-sm' : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm'}`}>
                                  {m.reply_to && (
                                    <div className={`border-l-2 pl-2 mb-1 text-[11px] max-w-full truncate ${mine ? 'border-white/40 text-white/70' : 'border-slate-300 text-slate-500'}`}>
                                      <span className="font-semibold">{m.reply_author || 'Member'}</span>: {m.reply_preview}
                                    </div>
                                  )}
                                  {m.body}
                                </div>
                                <ReactionChips reactions={m.reactions} myId={user?.user_id} onToggle={(e) => react(m, e)} />
                              </div>
                              {!mine && <MsgToolbar onReact={(e) => react(m, e)} onReply={() => setReplyTo(m)} align="left" />}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="border-t border-slate-100">
                    {replyTo && (
                      <div className="flex items-center gap-2 px-4 pt-2 text-xs text-slate-500" data-testid="reply-banner">
                        <Reply className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">Replying to <span className="font-semibold text-slate-700">{replyTo.from_name || 'Member'}</span>: {replyTo.body}</span>
                        <button onClick={() => setReplyTo(null)} data-testid="reply-cancel" className="ml-auto text-slate-400 hover:text-slate-700 shrink-0"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    )}
                    <div className="p-3 flex items-center gap-2">
                      <input value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') send(); }} placeholder={active.type === 'channel' ? `Message #${activeChannel?.label}` : 'Type a message…'} data-testid="inbox-input" className="flex-1 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20" />
                      <button onClick={send} data-testid="inbox-send" className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-slate-900 text-white hover:bg-slate-800"><Send className="w-4 h-4" /></button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CommunityInboxPage;
